import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  UtensilsCrossed,
  Clock,
  Loader2,
  ArrowRight,
  CreditCard,
  Crown,
  Calendar,
  AlertTriangle,
  Smartphone,
  Globe,
  ExternalLink,
  RefreshCw,
  CheckCircle2,
  Sparkles,
  Star,
  Shield,
} from "lucide-react";
import AppLayout from "../components/Layout/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { auth } from "../config/firebase";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const API_URL = import.meta.env.VITE_API_URL;
const HALAL_API_URL = import.meta.env.VITE_HALAL_API;

const PLAN_DETAILS = {
  basic: { name: "Basic", price: "$25/mo" },
  pro: { name: "Pro", price: "$75/mo" },
};

// Geometric Islamic pattern SVG for background texture
const GeometricPattern = () => (
  <svg
    className="halal-geo-pattern"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <pattern
        id="halalGeo"
        x="0"
        y="0"
        width="60"
        height="60"
        patternUnits="userSpaceOnUse"
      >
        <path
          d="M30 0L60 15L60 45L30 60L0 45L0 15Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.5"
          opacity="0.08"
        />
        <circle cx="30" cy="30" r="4" fill="none" stroke="currentColor" strokeWidth="0.3" opacity="0.06" />
        <path
          d="M30 10L50 22L50 38L30 50L10 38L10 22Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.3"
          opacity="0.05"
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#halalGeo)" />
  </svg>
);

// Decorative card wrapper with warm glass effect
function WarmCard({ children, className, delay = 0, accent = false, ...props }) {
  return (
    <div
      className={cn(
        "halal-card group relative overflow-hidden rounded-2xl border bg-card/80 backdrop-blur-md",
        "shadow-[0_2px_20px_-4px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_20px_-4px_rgba(0,0,0,0.3)]",
        "hover:shadow-[0_8px_40px_-8px_rgba(0,0,0,0.1)] dark:hover:shadow-[0_8px_40px_-8px_rgba(0,0,0,0.4)]",
        "transition-all duration-500 ease-out",
        accent && "halal-card-accent",
        className
      )}
      style={{
        animationDelay: `${delay}ms`,
        animationFillMode: "both",
      }}
      {...props}
    >
      {/* Subtle warm gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-transparent to-primary/[0.01]" />
      <div className="relative z-10 flex flex-col flex-1">{children}</div>
    </div>
  );
}

// Ornate section label
function SectionLabel({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/8 dark:bg-primary/15 ring-1 ring-primary/10">
        <Icon className="h-[18px] w-[18px] text-primary" />
      </div>
      <span className="halal-section-label text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground/70">
        {label}
      </span>
      <div className="h-px flex-1 bg-gradient-to-r from-border/60 to-transparent" />
    </div>
  );
}

export default function HalalDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [serverUser, setServerUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeError, setUpgradeError] = useState(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeDialogState, setUpgradeDialogState] = useState("confirm"); // confirm | loading | success | error
  const [scrollY, setScrollY] = useState(0);
  const [time, setTime] = useState(0);
  const containerRef = useRef(null);

  // Scroll-based and continuous background animation
  useEffect(() => {
    let animationFrameId;
    const animate = (timestamp) => {
      setTime(timestamp);
      animationFrameId = requestAnimationFrame(animate);
    };
    animationFrameId = requestAnimationFrame(animate);

    const handleScroll = () => {
      // Use the main scrollable element (the page itself)
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Load cached server user data on mount
  useEffect(() => {
    try {
      const cachedServerUser = localStorage.getItem("muslim_server_user");
      if (cachedServerUser) {
        setServerUser(JSON.parse(cachedServerUser));
      }
    } catch (err) {
      console.error("Error loading cached server user data:", err);
    }
  }, []);

  // Fetch fresh user data
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        setError(null);
        const idToken = await user.getIdToken(true);
        const resp = await fetch(`${API_URL}/v1/users/me`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        const data = await resp.json();
        if (!cancelled && resp.ok) {
          const userData = data.user || null;
          setServerUser(userData);
          if (userData) {
            localStorage.setItem(
              "muslim_server_user",
              JSON.stringify(userData)
            );
          }
        } else if (!cancelled) {
          setError("Failed to load your account data. Please try again.");
        }
      } catch (e) {
        console.error("Error loading user data:", e);
        if (!cancelled) setError("Network error. Please check your connection.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Determine user status
  const isPending =
    serverUser?.role === "halal" &&
    (serverUser?.status === "pending" || serverUser?.status === "applied") &&
    serverUser?.restaurantProfileComplete === true;

  const isApproved =
    serverUser?.role === "halal" &&
    serverUser?.status === "approved" &&
    serverUser?.halalId;

  const needsApplication =
    !serverUser ||
    (serverUser?.role !== "halal" && !serverUser?.restaurantProfileComplete);

  const isUnknownState =
    !loading &&
    !error &&
    !isPending &&
    !isApproved &&
    !needsApplication &&
    serverUser;

  // Fetch subscription when approved
  useEffect(() => {
    if (!isApproved || !auth.currentUser) return;
    let cancelled = false;

    const fetchSubscription = async () => {
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      try {
        const idToken = await auth.currentUser.getIdToken(true);
        const resp = await fetch(`${HALAL_API_URL}/v1/halal/subscription`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        const data = await resp.json();
        if (!cancelled) {
          if (resp.ok && data.subscription) {
            setSubscription(data.subscription);
          } else if (resp.status === 404) {
            setSubscription(null);
          } else {
            setSubscriptionError("Could not load subscription info.");
          }
        }
      } catch (e) {
        console.error("Error fetching subscription:", e);
        if (!cancelled)
          setSubscriptionError("Could not load subscription info.");
      } finally {
        if (!cancelled) setSubscriptionLoading(false);
      }
    };

    fetchSubscription();
    return () => {
      cancelled = true;
    };
  }, [isApproved]);

  // Open Stripe billing portal
  const handleManageBilling = async () => {
    if (!auth.currentUser) return;
    try {
      setBillingPortalLoading(true);
      const idToken = await auth.currentUser.getIdToken(true);
      const resp = await fetch(`${HALAL_API_URL}/v1/halal/billing-portal`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });
      const data = await resp.json();
      if (resp.ok && data.url) {
        window.location.href = data.url;
      } else {
        console.error("Failed to create portal session:", data);
        alert(
          "Unable to open billing portal. Please try again or contact support."
        );
      }
    } catch (e) {
      console.error("Error opening billing portal:", e);
      alert("Unable to open billing portal. Please try again.");
    } finally {
      setBillingPortalLoading(false);
    }
  };

  // Upgrade halal basic → pro directly via backend
  const handleUpgradeToPro = async () => {
    if (!auth.currentUser) return;
    try {
      setUpgradeLoading(true);
      setUpgradeError(null);
      setUpgradeDialogState("loading");
      const idToken = await auth.currentUser.getIdToken(true);
      const resp = await fetch(`${HALAL_API_URL}/v1/halal/upgrade-to-pro`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });
      const data = await resp.json();
      if (resp.ok && data.ok) {
        // Refresh subscription data to reflect the new tier
        const subResp = await fetch(`${HALAL_API_URL}/v1/halal/subscription`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        const subData = await subResp.json();
        if (subResp.ok && subData.subscription) {
          setSubscription(subData.subscription);
        }
        setUpgradeDialogState("success");
      } else {
        setUpgradeError(data.message || "Failed to upgrade. Please try again.");
        setUpgradeDialogState("error");
      }
    } catch (e) {
      console.error("Error upgrading to pro:", e);
      setUpgradeError("Something went wrong. Please try again.");
      setUpgradeDialogState("error");
    } finally {
      setUpgradeLoading(false);
    }
  };

  const plan = PLAN_DETAILS[subscription?.tier] || PLAN_DETAILS.basic;
  const isPro = subscription?.tier === "pro";
  const isActive = subscription?.status === "active";
  const isPastDue = subscription?.status === "past_due";
  const isCanceled = subscription?.status === "canceled";
  const willCancel = subscription?.cancelAtPeriodEnd;

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    return new Date(isoString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, "");
    let cleaned = digits;
    if (digits.length === 11 && digits.startsWith("1")) {
      cleaned = digits.substring(1);
    }
    if (cleaned.length === 10) {
      return `+1 (${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
    }
    return phone;
  };

  const restaurant = serverUser?.restaurantData;
  const hasAddress = restaurant?.address || restaurant?.city || restaurant?.state || restaurant?.zipCode;
  const streetLine = restaurant?.address || "";
  const cityLine = [restaurant?.city, restaurant?.state].filter(Boolean).join(", ");
  const zipLine = restaurant?.zipCode || "";

  // Scroll-driven parallax values
  const glow1Y = scrollY * 0.15;
  const glow2Y = scrollY * -0.1;
  const patternY = scrollY * 0.05;

  return (
    <AppLayout>
      <div
        ref={containerRef}
        className="halal-dashboard relative min-h-[calc(100vh-4rem)]"
      >
        {/* Full-page background layer — fixed so it spans entire viewport */}
        <div className="fixed inset-0 top-16 pointer-events-none overflow-hidden z-0">
          {/* Geometric pattern with scroll parallax */}
          <div
            className="absolute inset-0 text-foreground"
            style={{ transform: `translateY(${patternY}px)` }}
          >
            <GeometricPattern />
          </div>

          {/* Warm ambient glows with scroll parallax */}
          <div
            className="halal-glow halal-glow-1"
            style={{ transform: `translateY(${glow1Y}px)` }}
          />
          <div
            className="halal-glow halal-glow-2"
            style={{ transform: `translateY(${glow2Y}px)` }}
          />

          {/* Extra glow that moves with scroll for depth */}
          <div
            className="halal-glow halal-glow-3"
            style={{ transform: `translate(${scrollY * 0.08}px, ${scrollY * -0.12}px)` }}
          />

          {/* Scroll-reactive and continuous wave bands */}
          <div
            className="halal-wave-band"
            style={{
              top: '30%',
              transform: `translateX(${Math.sin(time * 0.0008) * 40}px)`,
            }}
          >
            <svg viewBox="0 0 1440 200" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="wave-grad-1" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="wave-grad-2" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d={`M0,100 C${240 + Math.sin(time * 0.001) * 60},${60 + Math.sin(time * 0.0012) * 40} ${480 + Math.cos(time * 0.0009) * 40},${140 + Math.cos(time * 0.0011) * 30} 720,100 C${960 + Math.sin(time * 0.001) * 50},${60 + Math.cos(time * 0.0013) * 35} ${1200 + Math.cos(time * 0.0008) * 30},${140 + Math.sin(time * 0.001) * 25} 1440,100 L1440,200 L0,200 Z`}
                fill="url(#wave-grad-1)"
                stroke="none"
                opacity="0.15"
              />
              <path
                d={`M0,120 C${200 + Math.cos(time * 0.0009) * 50},${80 + Math.cos(time * 0.0011) * 30} ${500 + Math.sin(time * 0.001) * 60},${160 + Math.sin(time * 0.0008) * 35} 720,120 C${940 + Math.cos(time * 0.0012) * 40},${80 + Math.sin(time * 0.001) * 25} ${1180 + Math.sin(time * 0.0009) * 35},${160 + Math.cos(time * 0.0011) * 30} 1440,120 L1440,200 L0,200 Z`}
                fill="url(#wave-grad-2)"
                stroke="none"
                opacity="0.08"
              />
            </svg>
          </div>

          <div
            className="halal-wave-band"
            style={{
              top: '65%',
              transform: `translateX(${Math.sin(time * 0.0006 + 2) * -35}px)`,
            }}
          >
            <svg viewBox="0 0 1440 200" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="wave-grad-3" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="wave-grad-4" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d={`M0,100 C${280 + Math.cos(time * 0.0007) * 50},${50 + Math.sin(time * 0.0009) * 40} ${520 + Math.sin(time * 0.0008) * 45},${150 + Math.cos(time * 0.001) * 30} 720,100 C${920 + Math.cos(time * 0.0009) * 55},${50 + Math.cos(time * 0.0007) * 35} ${1160 + Math.sin(time * 0.001) * 40},${150 + Math.sin(time * 0.0008) * 25} 1440,100 L1440,200 L0,200 Z`}
                fill="url(#wave-grad-3)"
                stroke="none"
                opacity="0.12"
              />
              <path
                d={`M0,80 C${300 + Math.sin(time * 0.0008) * 45},${120 + Math.cos(time * 0.001) * 30} ${540 + Math.cos(time * 0.0007) * 50},${40 + Math.sin(time * 0.0009) * 35} 720,80 C${900 + Math.sin(time * 0.0009) * 40},${120 + Math.sin(time * 0.0008) * 25} ${1140 + Math.cos(time * 0.001) * 35},${40 + Math.cos(time * 0.0007) * 30} 1440,80 L1440,200 L0,200 Z`}
                fill="url(#wave-grad-4)"
                stroke="none"
                opacity="0.06"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-16">
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-32 animate-fade-in">
              <div className="relative mb-8">
                <div className="h-20 w-20 rounded-2xl bg-primary/8 dark:bg-primary/15 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
                <div className="absolute -inset-2 rounded-3xl border border-primary/10 animate-pulse" />
              </div>
              <p className="halal-body-text text-base text-muted-foreground font-medium tracking-wide">
                Loading your dashboard...
              </p>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <WarmCard className="animate-fade-in-up border-destructive/20" delay={0}>
              <div className="px-7 py-12 sm:px-10 text-center">
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-destructive/8 dark:bg-destructive/15">
                  <AlertTriangle className="h-8 w-8 text-destructive" />
                </div>
                <h2 className="halal-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                  Something went wrong
                </h2>
                <p className="halal-body-text text-base text-muted-foreground mb-8 max-w-sm mx-auto leading-relaxed">
                  {error}
                </p>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="gap-2.5 h-12 px-7 rounded-xl font-medium text-base"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </Button>
              </div>
            </WarmCard>
          )}

          {/* Pending Review State */}
          {!loading && !error && isPending && (
            <WarmCard className="animate-fade-in-up" delay={0} accent>
              <div className="relative">
                {/* Amber accent strip */}
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400/80 via-amber-500 to-orange-400/80" />

                <div className="px-7 py-14 sm:px-10 text-center">
                  {/* Animated clock icon */}
                  <div className="relative mx-auto mb-8 w-24 h-24">
                    <div className="absolute inset-0 rounded-full bg-amber-100/60 dark:bg-amber-900/20 animate-pulse" />
                    <div className="absolute inset-2 rounded-full bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
                      <Clock className="h-10 w-10 text-amber-600 dark:text-amber-400" />
                    </div>
                    {/* Orbiting dot */}
                    <div className="halal-orbit absolute inset-0">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-amber-500 shadow-lg shadow-amber-500/40" />
                    </div>
                  </div>

                  <h2 className="halal-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                    Application Under Review
                  </h2>
                  <p className="halal-body-text text-base text-muted-foreground max-w-md mx-auto mb-9 leading-relaxed">
                    Our team is reviewing your halal certification and business
                    details. You'll receive an email once approved.
                  </p>

                  <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-amber-50/80 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-800/30 text-amber-700 dark:text-amber-300 text-sm font-semibold">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-50" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
                    </span>
                    Review in progress
                  </div>
                </div>
              </div>
            </WarmCard>
          )}

          {/* Approved State - Main Dashboard */}
          {!loading && !error && isApproved && (
            <div className="space-y-6">
              {/* Welcome Header — full width */}
              <div className="animate-fade-in-up mb-2">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h1 className="halal-body-text text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                      {restaurant?.restaurantName || "My Restaurant"}
                    </h1>
                    <div className="flex items-center gap-3 mt-3">
                      <span className="halal-body-text text-muted-foreground capitalize text-base">
                        {restaurant?.businessType || "Restaurant"}
                      </span>
                      <span className="text-border">|</span>
                      <Badge className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300 border border-emerald-200/50 dark:border-emerald-800/30 gap-1.5 font-semibold text-sm px-3 py-0.5">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Verified Halal
                      </Badge>
                    </div>
                  </div>
                </div>
                {/* Decorative divider */}
                <div className="mt-6 flex items-center gap-3">
                  <div className="h-px flex-1 bg-gradient-to-r from-primary/30 via-primary/10 to-transparent" />
                  <Star className="h-3 w-3 text-primary/30" />
                  <div className="h-px flex-1 bg-gradient-to-l from-primary/30 via-primary/10 to-transparent" />
                </div>
              </div>

              {/* Two-column grid on desktop */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Business Info Card */}
                {(hasAddress || restaurant?.phone || restaurant?.website) && (
                  <WarmCard className="animate-fade-in-up flex flex-col" delay={80}>
                    <div className="p-6 sm:p-7 flex flex-col flex-1">
                      <SectionLabel icon={UtensilsCrossed} label="Business Details" />

                      {/* Address block */}
                      {hasAddress && (
                        <div className="group/addr relative rounded-xl bg-muted/30 dark:bg-muted/20 border border-border/50 p-4 mb-4 hover:border-primary/20 transition-colors">
                          <p className="halal-body-text text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground/50 mb-1.5">Address</p>
                          <div className="halal-body-text text-[15px] text-foreground/85 leading-relaxed">
                            {streetLine && <div>{streetLine}</div>}
                            {cityLine && <div>{cityLine}</div>}
                            {zipLine && <div>{zipLine}</div>}
                          </div>
                        </div>
                      )}

                      {/* Phone & Website — side by side when both exist */}
                      <div className={cn(
                        "grid gap-4",
                        restaurant?.phone && restaurant?.website ? "grid-cols-2" : "grid-cols-1"
                      )}>
                        {restaurant?.phone && (
                          <div className="group/phone relative rounded-xl bg-muted/30 dark:bg-muted/20 border border-border/50 p-4 hover:border-primary/20 transition-colors">
                            <p className="halal-body-text text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground/50 mb-1">Phone</p>
                            <a
                              href={`tel:${restaurant.phone}`}
                              className="halal-body-text text-[15px] text-foreground/85 hover:text-primary transition-colors"
                            >
                              {formatPhone(restaurant.phone)}
                            </a>
                          </div>
                        )}
                        {restaurant?.website && (
                          <div className="group/web relative rounded-xl bg-muted/30 dark:bg-muted/20 border border-border/50 p-4 hover:border-primary/20 transition-colors">
                            <div className="flex flex-col items-start gap-3">
                              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/8 dark:bg-primary/15 ring-1 ring-primary/10 group-hover/web:ring-primary/25 transition-all">
                                <Globe className="h-[18px] w-[18px] text-primary" />
                              </div>
                              <div className="min-w-0 w-full">
                                <p className="halal-body-text text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground/50 mb-1">Website</p>
                                <a
                                  href={
                                    restaurant.website.startsWith("http")
                                      ? restaurant.website
                                      : `https://${restaurant.website}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="halal-body-text text-[15px] text-primary hover:text-primary/80 hover:underline underline-offset-4 truncate block font-medium transition-colors"
                                >
                                  {restaurant.website.replace(/^https?:\/\//, '')}
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Business type & Halal ID — fills remaining vertical space */}
                      <div className="mt-auto pt-5">
                        <div className="rounded-xl bg-gradient-to-br from-primary/[0.04] via-primary/[0.02] to-transparent border border-primary/10 p-4">
                          <p className="halal-body-text text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground/50 mb-1">Business Type</p>
                          <p className="halal-body-text text-[15px] text-foreground/85 capitalize font-medium">
                            {restaurant?.businessType || "Restaurant"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </WarmCard>
                )}

                {/* Subscription Card */}
                <WarmCard className="animate-fade-in-up" delay={160}>
                <div className="p-6 sm:p-7">
                  <SectionLabel icon={CreditCard} label="Subscription" />

                  {subscriptionLoading ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-3">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/50" />
                      <span className="text-sm text-muted-foreground/50">Loading subscription...</span>
                    </div>
                  ) : subscriptionError ? (
                    <div className="text-center py-8">
                      <p className="halal-body-text text-base text-muted-foreground mb-5">
                        {subscriptionError}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.location.reload()}
                        className="gap-2 rounded-xl text-sm"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Retry
                      </Button>
                    </div>
                  ) : subscription ? (
                    <>
                      {/* Plan Info Row */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border/50 mb-5">
                        <div className="flex items-center gap-4">
                          <div className={cn(
                            "flex h-12 w-12 items-center justify-center rounded-xl transition-colors",
                            isPro
                              ? "bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/20"
                              : "bg-primary/8"
                          )}>
                            {isPro ? (
                              <Crown className="h-5 w-5 text-primary" />
                            ) : (
                              <Sparkles className="h-5 w-5 text-primary" />
                            )}
                          </div>
                          <div>
                            <p className="halal-body-text font-bold text-base">
                              {plan.name} Plan
                            </p>
                            <p className="halal-body-text text-sm text-muted-foreground mt-0.5 tracking-wide">
                              {plan.price}
                            </p>
                          </div>
                        </div>
                        <Badge
                          variant="secondary"
                          className={cn(
                            "font-semibold text-sm px-3.5 py-1 border",
                            isActive && !willCancel
                              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300 border-emerald-200/50 dark:border-emerald-800/30"
                              : isPastDue
                                ? "bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-300 border-red-200/50 dark:border-red-800/30"
                                : isCanceled
                                  ? "bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-300 border-red-200/50 dark:border-red-800/30"
                                  : willCancel
                                    ? "bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300 border-amber-200/50 dark:border-amber-800/30"
                                    : "border-border"
                          )}
                        >
                          {isActive && !willCancel && "Active"}
                          {isActive && willCancel && "Canceling"}
                          {isPastDue && "Past Due"}
                          {isCanceled && "Canceled"}
                          {!isActive &&
                            !isPastDue &&
                            !isCanceled &&
                            !willCancel &&
                            "—"}
                        </Badge>
                      </div>

                      {/* Billing Period */}
                      {subscription?.currentPeriodEnd && (
                        <div className="flex items-start gap-3 text-[15px] text-muted-foreground mb-5 px-1">
                          <Calendar className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <div className="halal-body-text">
                            <span>
                              {willCancel || isCanceled
                                ? "Access until"
                                : "Next billing"}
                            </span>
                            <p className="font-bold text-foreground mt-0.5">
                              {formatDate(subscription.currentPeriodEnd)}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Past Due Warning */}
                      {isPastDue && (
                        <div className="rounded-xl bg-red-50/80 dark:bg-red-900/15 p-4 mb-5 border border-red-200/40 dark:border-red-900/30">
                          <div className="flex items-start gap-3.5">
                            <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
                              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                            </div>
                            <div>
                              <p className="halal-body-text text-[15px] font-bold text-red-700 dark:text-red-300">
                                Payment failed
                              </p>
                              <p className="halal-body-text text-sm text-red-600/80 dark:text-red-400/80 mt-1 leading-relaxed">
                                Update your payment method to keep your listing
                                active.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Cancellation Notice */}
                      {willCancel && !isPastDue && (
                        <div className="rounded-xl bg-amber-50/80 dark:bg-amber-900/15 p-4 mb-5 border border-amber-200/40 dark:border-amber-900/30">
                          <div className="flex items-start gap-3.5">
                            <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30">
                              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div>
                              <p className="halal-body-text text-[15px] font-bold text-amber-700 dark:text-amber-300">
                                Subscription ending
                              </p>
                              <p className="halal-body-text text-sm text-amber-600/80 dark:text-amber-400/80 mt-1 leading-relaxed">
                                Your listing will be hidden after{" "}
                                {formatDate(subscription?.currentPeriodEnd)}.
                                Reactivate anytime from the billing portal.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Upgrade to Pro Button — shown for active basic plan users */}
                      {!isPro && isActive && !willCancel && (
                        <div className="mb-3">
                          <Button
                            onClick={() => {
                              setUpgradeDialogState("confirm");
                              setUpgradeError(null);
                              setUpgradeDialogOpen(true);
                            }}
                            disabled={upgradeLoading}
                            className="w-full gap-2.5 h-12 rounded-xl font-semibold text-[15px]"
                          >
                            {upgradeLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Crown className="h-4 w-4" />
                            )}
                            Upgrade to Pro
                          </Button>
                          {upgradeError && (
                            <p className="text-sm text-destructive mt-2 text-center">
                              {upgradeError}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Upgrade Confirmation Dialog */}
                      <Dialog
                        open={upgradeDialogOpen}
                        onOpenChange={(open) => {
                          if (!open && upgradeDialogState !== "loading") {
                            setUpgradeDialogOpen(false);
                            // Reset to confirm state after closing
                            setTimeout(() => setUpgradeDialogState("confirm"), 200);
                          }
                        }}
                      >
                        <DialogContent className="sm:max-w-md">
                          {/* Confirm State */}
                          {upgradeDialogState === "confirm" && (
                            <>
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                  <Crown className="h-5 w-5 text-primary" />
                                  Upgrade to Pro
                                </DialogTitle>
                                <DialogDescription className="text-left pt-2 space-y-3">
                                  <span className="block">
                                    You are upgrading from the <span className="font-semibold text-foreground">Basic Plan ($25/mo)</span> to the <span className="font-semibold text-foreground">Pro Plan ($75/mo)</span>.
                                  </span>
                                  <span className="block">
                                    You will only be charged the prorated difference for the remainder of this billing cycle.
                                  </span>
                                  <span className="block text-xs">
                                    By pressing "Confirm Upgrade" you agree to the{" "}
                                    <a
                                      href="/terms"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary underline hover:text-primary/80"
                                    >
                                      Terms and Conditions
                                    </a>.
                                  </span>
                                </DialogDescription>
                              </DialogHeader>
                              <DialogFooter className="gap-2 sm:gap-0">
                                <Button
                                  variant="outline"
                                  onClick={() => setUpgradeDialogOpen(false)}
                                  className="rounded-xl"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={handleUpgradeToPro}
                                  className="gap-2 rounded-xl"
                                >
                                  <Crown className="h-4 w-4" />
                                  Confirm Upgrade
                                </Button>
                              </DialogFooter>
                            </>
                          )}

                          {/* Loading State */}
                          {upgradeDialogState === "loading" && (
                            <div className="flex flex-col items-center justify-center py-8 px-4">
                              <div className="relative mb-6">
                                <div className="h-16 w-16 rounded-2xl bg-primary/8 dark:bg-primary/15 flex items-center justify-center">
                                  <Loader2 className="h-7 w-7 animate-spin text-primary" />
                                </div>
                                <div className="absolute -inset-1.5 rounded-2xl border border-primary/10 animate-pulse" />
                              </div>
                              <p className="halal-body-text text-base font-semibold text-foreground mb-1">
                                Upgrading your plan...
                              </p>
                              <p className="halal-body-text text-sm text-muted-foreground text-center">
                                This may take a moment. Please don't close this window.
                              </p>
                            </div>
                          )}

                          {/* Success State */}
                          {upgradeDialogState === "success" && (
                            <div className="flex flex-col items-center justify-center py-8 px-4">
                              <div className="relative mb-6">
                                <div className="h-16 w-16 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                                  <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
                                </div>
                              </div>
                              <p className="halal-body-text text-base font-semibold text-foreground mb-1">
                                Upgrade successful!
                              </p>
                              <p className="halal-body-text text-sm text-muted-foreground text-center mb-6">
                                You're now on the Pro plan. Enjoy the premium features.
                              </p>
                              <Button
                                onClick={() => {
                                  setUpgradeDialogOpen(false);
                                  setTimeout(() => setUpgradeDialogState("confirm"), 200);
                                }}
                                className="gap-2 rounded-xl w-full sm:w-auto"
                              >
                                <Crown className="h-4 w-4" />
                                Done
                              </Button>
                            </div>
                          )}

                          {/* Error State */}
                          {upgradeDialogState === "error" && (
                            <div className="flex flex-col items-center justify-center py-8 px-4">
                              <div className="relative mb-6">
                                <div className="h-16 w-16 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                                  <AlertTriangle className="h-7 w-7 text-red-600 dark:text-red-400" />
                                </div>
                              </div>
                              <p className="halal-body-text text-base font-semibold text-foreground mb-1">
                                Upgrade failed
                              </p>
                              <p className="halal-body-text text-sm text-muted-foreground text-center mb-6">
                                {upgradeError || "Something went wrong. Please try again."}
                              </p>
                              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setUpgradeDialogOpen(false);
                                    setTimeout(() => setUpgradeDialogState("confirm"), 200);
                                  }}
                                  className="rounded-xl"
                                >
                                  Dismiss
                                </Button>
                                <Button
                                  onClick={() => {
                                    setUpgradeDialogState("confirm");
                                  }}
                                  className="gap-2 rounded-xl"
                                >
                                  <RefreshCw className="h-4 w-4" />
                                  Try Again
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>

                      {/* Manage Billing Button */}
                      <Button
                        onClick={handleManageBilling}
                        disabled={billingPortalLoading}
                        className={cn(
                          "w-full gap-2.5 h-12 rounded-xl font-semibold text-[15px]",
                          !isPastDue && "halal-btn-outline"
                        )}
                        variant={isPastDue ? "destructive" : "outline"}
                      >
                        {billingPortalLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ExternalLink className="h-4 w-4" />
                        )}
                        {isPastDue
                          ? "Update Payment Method"
                          : "Manage Subscription"}
                      </Button>
                      {!isPro && isActive && !willCancel && (
                        <p className="text-xs text-primary mt-3 text-center">
                          When upgrading to Pro, you'll only be charged the prorated difference for the rest of this billing cycle.
                        </p>
                      )}
                    </>
                  ) : (
                    /* No subscription found */
                    <div className="text-center py-8">
                      <p className="halal-body-text text-base text-muted-foreground mb-1">
                        No active subscription found.
                      </p>
                      <p className="halal-body-text text-sm text-muted-foreground mb-6">
                        Your subscription may still be setting up. If this
                        persists, contact support.
                      </p>
                      <Button
                        onClick={handleManageBilling}
                        disabled={billingPortalLoading}
                        variant="outline"
                        className="gap-2.5 h-12 rounded-xl font-medium text-[15px]"
                      >
                        {billingPortalLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CreditCard className="h-4 w-4" />
                        )}
                        View Billing
                      </Button>
                    </div>
                  )}
                </div>
                </WarmCard>
              </div>

              {/* Manage via App Card — full width */}
              <WarmCard className="animate-fade-in-up cursor-pointer" delay={240}>
                <div className="p-5 sm:p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary/8 dark:bg-primary/15 ring-1 ring-primary/10 group-hover:bg-primary/12 group-hover:ring-primary/20 transition-all">
                      <Smartphone className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="halal-body-text font-bold text-base">Manage in the App</p>
                      <p className="halal-body-text text-sm text-muted-foreground mt-0.5">
                        Update hours, photos, menu & more
                      </p>
                    </div>
                  </div>
                  {/* App store links */}
                  <div className="flex gap-2.5 mt-4 ml-16">
                    <a
                      href="https://apps.apple.com/us/app/muslim-prayer-companion/id6745054019"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors px-4 py-2 rounded-lg bg-primary/5 hover:bg-primary/10 border border-primary/10"
                    >
                      iOS
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.psu.pdxmsa&pcampaignid=web_share"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors px-4 py-2 rounded-lg bg-primary/5 hover:bg-primary/10 border border-primary/10"
                    >
                      Android
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </WarmCard>
            </div>
          )}

          {/* No Application State */}
          {!loading && !error && needsApplication && (
            <WarmCard className="animate-fade-in-up" delay={0} accent>
              <div className="relative">
                {/* Primary accent strip */}
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />

                <div className="px-7 py-16 sm:px-10 text-center">
                  {/* Ornate icon treatment */}
                  <div className="relative mx-auto mb-8 w-28 h-28">
                    <div className="absolute inset-0 rounded-full border-2 border-dashed border-primary/15 halal-spin-slow" />
                    <div className="absolute inset-3 rounded-full bg-primary/5 dark:bg-primary/10" />
                    <div className="absolute inset-5 rounded-full bg-primary/8 dark:bg-primary/15 flex items-center justify-center">
                      <UtensilsCrossed className="h-9 w-9 text-primary" />
                    </div>
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-2.5 w-2.5 rounded-full bg-primary/30" />
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-2.5 w-2.5 rounded-full bg-primary/30" />
                    <div className="absolute top-1/2 -left-1 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-primary/30" />
                    <div className="absolute top-1/2 -right-1 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-primary/30" />
                  </div>

                  <h2 className="halal-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                    List Your Halal Business
                  </h2>
                  <p className="halal-body-text text-base text-muted-foreground max-w-md mx-auto mb-10 leading-relaxed">
                    Join the Muslim+ community and reach thousands of customers
                    looking for halal options in their area.
                  </p>

                  <Button
                    onClick={() => navigate("/halal-signup")}
                    size="lg"
                    className="gap-3 h-14 px-10 rounded-xl font-bold text-lg w-full sm:w-auto shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-shadow"
                  >
                    Start Application
                    <ArrowRight className="h-5 w-5" />
                  </Button>

                  {/* Trust indicators */}
                  <div className="flex items-center justify-center gap-6 mt-10 text-sm text-muted-foreground/60">
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Quick setup
                    </span>
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Cancel anytime
                    </span>
                  </div>
                </div>
              </div>
            </WarmCard>
          )}

          {/* Unknown / Edge Case State */}
          {isUnknownState && (
            <WarmCard className="animate-fade-in-up" delay={0}>
              <div className="px-7 py-12 sm:px-10 text-center">
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-muted/40">
                  <AlertTriangle className="h-8 w-8 text-muted-foreground" />
                </div>
                <h2 className="halal-heading text-2xl font-bold tracking-tight mb-3">
                  Account Status Unknown
                </h2>
                <p className="halal-body-text text-base text-muted-foreground max-w-md mx-auto mb-8 leading-relaxed">
                  We couldn't determine your account status. This may be a
                  temporary issue. Try refreshing, or contact support if it
                  persists.
                </p>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="gap-2.5 h-12 px-7 rounded-xl font-medium text-base"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
              </div>
            </WarmCard>
          )}
        </div>

        {/* Footer */}
        <footer className="relative z-10 border-t border-border/50 backdrop-blur-sm bg-background/30 mt-16">
          <div className="mx-auto max-w-5xl px-5 py-14 sm:px-8">
            <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
              {/* Brand */}
              <div className="lg:col-span-2">
                <h3 className="mb-4 bg-gradient-to-r from-[#bf9a2f] via-[#e6c86e] to-[#bf9a2f] bg-clip-text text-2xl font-bold text-transparent">
                  Muslim+
                </h3>
                <p className="halal-body-text mb-5 max-w-md text-sm leading-relaxed text-muted-foreground">
                  Connecting Muslims with their community through technology and
                  faith. Find prayer times, discover nearby masajid, stay updated
                  with events, and explore halal restaurants.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="gap-1 px-3 py-1">
                    <Shield className="h-3 w-3" />
                    Trusted Platform
                  </Badge>
                  <Badge variant="outline" className="gap-1 px-3 py-1">
                    <Sparkles className="h-3 w-3" />
                    Verified Content
                  </Badge>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="halal-body-text mb-4 text-base font-semibold">Quick Links</h3>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => navigate("/masjids")}
                    className="halal-body-text text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    Find Masajid
                  </button>
                  <button
                    onClick={() => navigate("/events-public")}
                    className="halal-body-text text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    Browse Events
                  </button>
                  <button
                    onClick={() => navigate("/settings")}
                    className="halal-body-text text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    Settings
                  </button>
                  <button
                    onClick={() => navigate("/policy")}
                    className="halal-body-text text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    Privacy Policy
                  </button>
                </div>
              </div>

              {/* Resources */}
              <div>
                <h3 className="halal-body-text mb-4 text-base font-semibold">Resources</h3>
                <div className="flex flex-col gap-3">
                  <a
                    href="https://apps.apple.com/us/app/muslim-prayer-companion/id6745054019"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="halal-body-text text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    Download iOS App
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.psu.pdxmsa&pcampaignid=web_share"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="halal-body-text text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    Download Android App
                  </a>
                  <a
                    href="https://www.instagram.com/muslimplus.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="halal-body-text text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    Follow us on Instagram
                  </a>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="mt-10 border-t border-border/50 pt-7">
              <div className="flex flex-col items-center justify-between gap-3 md:flex-row">
                <p className="halal-body-text text-sm text-muted-foreground">
                  &copy; {new Date().getFullYear()} Muslim+. All rights reserved.
                </p>
                <span className="halal-body-text text-xs text-muted-foreground/60">
                  Made with love for the Muslim Community
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </AppLayout>
  );
}
